import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { verifyEmail, getProfile } from '../../redux/reducers/authSlice';
import { Spinner, Button, Card } from 'react-bootstrap';
import { LuCircleCheck, LuArrowRight, LuMailCheck } from 'react-icons/lu';
import { toast } from 'react-toastify';

const VerifyEmailHandler = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = searchParams.get("token");

    // 'loading' | 'verified' | 'already-verified' | 'error'
    const [status, setStatus] = useState('loading');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            return;
        }

        dispatch(verifyEmail(token)).then((res) => {
            if (res.meta.requestStatus === 'fulfilled') {
                setStatus('verified');
                handlePostVerifyRedirection();
            } else {
                if (res.payload?.message?.includes("already verified") || res.payload?.code === "ALREADY_VERIFIED") {
                    setStatus('already-verified');
                } else {
                    setStatus('error');
                    toast.error(res.payload?.message || "Link invalid or expired.");
                }
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handlePostVerifyRedirection = () => {
        const authToken = localStorage.getItem("token");
        if (authToken) {
            dispatch(getProfile()).then((profileRes) => {
                const user = profileRes.payload;
                setTimeout(() => {
                    navigate(user?.hasCompletedOnboarding ? "/" : "/onboarding/setup-business");
                }, 2000); // 2-second delay so they see the success message
            });
        }
    };

    const goToDashboard = () => navigate("/");

    // --- UI STATES ---

    // 1. LOADING
    if (status === 'loading') {
        return (
            <div className="vh-100 d-flex flex-column align-items-center justify-content-center bg-light">
                <Spinner animation="border" variant="brand" className="mb-3" />
                <h5 className="fw-bold">Verifying your account...</h5>
            </div>
        );
    }

    // 2. SUCCESS (First Time)
    if (status === 'verified') {
        return (
            <div className="vh-100 d-flex align-items-center justify-content-center bg-light px-3">
                <Card className="border-0 shadow-sm rounded-4 p-5 text-center" style={{ maxWidth: '450px' }}>
                    <div className="text-success mb-4">
                        <LuCircleCheck size={80} />
                    </div>
                    <h3 className="fw-bold">Email Verified!</h3>
                    <p className="text-muted">Thank you for verifying your email. Redirecting you to your dashboard...</p>
                    <Spinner animation="grow" variant="success" size="sm" />
                </Card>
            </div>
        );
    }

    // 3. ALREADY VERIFIED (Second Time)
    if (status === 'already-verified') {
        return (
            <div className="vh-100 d-flex align-items-center justify-content-center bg-light px-3">
                <Card className="border-0 shadow-sm rounded-4 p-5 text-center" style={{ maxWidth: '450px' }}>
                    <div className="text-brand mb-4">
                        <LuMailCheck size={80} />
                    </div>
                    <h3 className="fw-bold">Already Verified</h3>
                    <p className="text-muted mb-4">
                        This email address has already been confirmed. You can head straight to your dashboard.
                    </p>
                    <Button
                        variant="brand"
                        className="w-100 rounded-pill py-3 fw-bold d-flex align-items-center justify-content-center gap-2"
                        onClick={goToDashboard}
                    >
                        Go to Dashboard <LuArrowRight size={20} />
                    </Button>
                </Card>
            </div>
        );
    }

    // 4. ERROR
    return (
        <div className="vh-100 d-flex align-items-center justify-content-center bg-light px-3">
            <Card className="border-0 shadow-sm rounded-4 p-5 text-center" style={{ maxWidth: '450px' }}>
                <h3 className="fw-bold text-danger">Invalid Link</h3>
                <p className="text-muted mb-4">This link is either expired or invalid. Please request a new verification email.</p>
                <Button variant="outline-dark" className="rounded-pill px-4" onClick={() => navigate('/login')}>
                    Back to Login
                </Button>
            </Card>
        </div>
    );
};

export default VerifyEmailHandler;
